import {
	PagedData,
	PagingDefinition,
	SortingDefinition,
	UserRelatedTokenFilter,
	UserTaskToken,
	UserTaskTokenDetails,
} from '@lg/shared'
import { AppContext } from '../app-context.js'
import { AppTrpcServiceInjectionToken } from '../services/app-trpc.service.js'
import { FrontendUserBridge } from './frontend-user-bridge.js'
import { FrontendScreenBridge } from './frontend-screen-bridge.js'
import { ProcessService } from '../services/process.service.js'
import { jsonMapper } from '@lg/shared'
import { NavigationExtras } from '@angular/router'
import { DevBarService } from '../services/dev-bar.service.js'
import { FormCanDeactivate } from '../directives/form-can-deactivate.js'

export class FrontendProcessBridge {
	static async getTaskById(
		tokenId: string,
	): Promise<UserTaskToken | undefined> {
		const trpc = AppContext.injector.get(AppTrpcServiceInjectionToken)
		let task = await trpc.client.process.getTaskById.query({ tokenId })
		task = jsonMapper.deserializeFromObject(task, UserTaskToken)
		return task
	}

	static async getTaskDetailsById(
		tokenId: string,
		includeAssignments: boolean,
	): Promise<UserTaskTokenDetails | undefined> {
		const trpc = AppContext.injector.get(AppTrpcServiceInjectionToken)
		let [taskDetails] = await trpc.client.process.getTaskDetailsByIds.query({
			tokenIds: [tokenId],
			includeAssignments,
		})
		taskDetails = jsonMapper.deserializeFromObject(
			taskDetails,
			UserTaskTokenDetails,
		)
		return taskDetails
	}

	static async getAllTasksForUser(
		filter: UserRelatedTokenFilter,
	): Promise<UserTaskToken[]> {
		const trpc = AppContext.injector.get(AppTrpcServiceInjectionToken)
		const result = await trpc.client.process.getTasksForUser.query({
			filter,
			paging: undefined,
		})
		result.list = jsonMapper.deserializeFromObject(result.list, UserTaskToken)
		return result.list
	}

	static async getPagedTasksForUser(
		filter: UserRelatedTokenFilter,
		paging: PagingDefinition,
		sorting?: SortingDefinition<keyof UserTaskToken>,
	): Promise<PagedData<UserTaskToken>> {
		const trpc = AppContext.injector.get(AppTrpcServiceInjectionToken)
		const result = await trpc.client.process.getTasksForUser.query({
			filter,
			paging,
			sorting,
		})
		result.list = jsonMapper.deserializeFromObject(result.list, UserTaskToken)
		return result
	}

	static async assignTaskToCurrentUser(tokenId: string) {
		const userId = FrontendUserBridge.currentUser?.id
		if (!userId) throw new Error('You are currently not logged in')
		return FrontendProcessBridge.assignTaskToUser(tokenId, userId, false)
	}

	static async assignCurrentTaskToCurrentUser() {
		const tokenId = FrontendProcessBridge.currentTaskTokenId
		if (!tokenId) throw new Error('You are not currently in a process task')
		return FrontendProcessBridge.assignTaskToCurrentUser(tokenId)
	}

	static async assignTaskToUser(
		tokenId: string,
		toUserIdOrUsername: string | undefined,
		reassignIfAssigned = false,
	) {
		const trpc = AppContext.injector.get(AppTrpcServiceInjectionToken)
		const result = await trpc.client.process.assignTaskToUser.mutate({
			tokenId,
			toUserIdOrUsername,
			reassignIfAssigned,
		})

		if (tokenId == FrontendProcessBridge.currentTaskTokenId) {
			AppContext.injector.get(ProcessService).tokenId.set(null)
			AppContext.injector.get(ProcessService).tokenId.set(tokenId) // trigger reload of task details
		}

		return result
	}

	static async unassignTask(tokenId: string) {
		const trpc = AppContext.injector.get(AppTrpcServiceInjectionToken)
		await trpc.client.process.unassignTask.mutate({
			tokenId,
		})

		if (tokenId == FrontendProcessBridge.currentTaskTokenId) {
			AppContext.injector.get(ProcessService).tokenId.set(null)
			AppContext.injector.get(ProcessService).tokenId.set(tokenId) // trigger reload of task details
		}
	}

	static async getPossibleTaskAssignees(tokenId: string) {
		const trpc = AppContext.injector.get(AppTrpcServiceInjectionToken)
		// const result = await trpc.client.process.getPossibleTaskAssignees.query({
		// 	tokenId,
		// })
		// return result
	}

	static async navigateToTask(
		tokenOrId: UserTaskToken | string,
		inNewWindow: boolean | string,
		extras?: NavigationExtras,
	) {
		if (typeof tokenOrId == 'string') {
			const token = await FrontendProcessBridge.getTaskById(tokenOrId)
			if (!token) throw new Error('Task not found: ' + tokenOrId)
			tokenOrId = token
		}

		if (!tokenOrId.screenModuleId) {
			tokenOrId = new UserTaskToken({
				...tokenOrId,
				screenModuleId: AppContext.appModuleId,
			})
		}

		if (!inNewWindow && tokenOrId.screenModuleId == AppContext.appModuleId) {
			FormCanDeactivate.unregisterAllForms()
			AppContext.router.navigate(['t', tokenOrId.id], extras)
			return
		}
		// alert('Navigate to URL: ' + tokenOrId.getUrl())
		FrontendScreenBridge.openUrl(tokenOrId.getUrl(), inNewWindow)
	}

	static async driveProcess(action: string, data: any, tokenId?: string) {
		const token =
			await FrontendProcessBridge.driveProcessAndGetFirstUserTaskToken(
				action,
				data,
				tokenId,
				-1,
				-1,
			)
		return jsonMapper.deserializeFromObject(token, UserTaskToken)
	}

	static async driveProcessAndFollowNextUserTask(
		action: string,
		data: any,
		tokenId?: string,
		taskTimeoutMs?: number,
		taskDebounceMs?: number,
		extras?: NavigationExtras,
	) {
		const result =
			await FrontendProcessBridge.driveProcessAndGetFirstUserTaskToken(
				action,
				data,
				tokenId,
				taskTimeoutMs,
				taskDebounceMs,
			)
		const shouldFollowTask =
			!!result?.id &&
			(!AppContext.injector.get(DevBarService).isHomeOnSwimlaneChange() ||
				result.swimlaneId ==
					FrontendProcessBridge.currentTaskDetails?.swimlaneId)
		if (shouldFollowTask) {
			await FrontendProcessBridge.navigateToTask(result.id, false, {
				replaceUrl: true,
				...extras,
			})
		} else {
			// TODO: redirect to tasks screen. TBD how this will be set; most likely on a per-process level
			FormCanDeactivate.unregisterAllForms()
			window.location.replace(
				window.location.href.replace(/\/t\/[a-f0-9-]*$/i, ''),
			)
		}
	}

	static async driveProcessAndGetFirstUserTaskToken(
		action: string,
		data: any,
		tokenId?: string,
		taskTimeoutMs?: number,
		taskDebounceMs?: number,
	) {
		if (!tokenId)
			tokenId = AppContext.injector.get(ProcessService).tokenId() ?? undefined
		if (!tokenId)
			throw new Error('Cannot drive process forward without a tokenId')

		const trpc = AppContext.resolve(AppTrpcServiceInjectionToken)
		data = jsonMapper.serializeToObject(data)
		const token = await trpc.client.process.driveProcess.mutate({
			tokenId,
			action,
			data,
			getFirstUserTaskToken:
				(taskTimeoutMs ?? 0) > -1 && (taskDebounceMs ?? 0) > -1
					? {
							taskTimeoutMs,
							taskDebounceMs,
						}
					: undefined,
		})
		return jsonMapper.deserializeFromObject(token, UserTaskToken)
	}

	static async cancelProcessFromTask(
		tokenId: string,
		redirectToHomeScreen = false,
	) {
		const trpc = AppContext.injector.get(AppTrpcServiceInjectionToken)
		await trpc.client.process.cancelProcessFromTask.mutate({
			tokenId,
		})
		if (redirectToHomeScreen) {
			FormCanDeactivate.unregisterAllForms()
			AppContext.router.navigate(['/'])
		}
	}

	static async cancelCurrentProcess() {
		const tokenId = FrontendProcessBridge.currentTaskTokenId
		if (!tokenId) throw new Error('You are not currently in a process task')
		return FrontendProcessBridge.cancelProcessFromTask(tokenId, true)
	}

	static get currentTaskTokenId() {
		return AppContext.injector.get(ProcessService).tokenId()
	}

	static get currentTaskDetails() {
		return AppContext.injector.get(ProcessService).taskDetails()
	}

	static get isProcessTask() {
		return AppContext.injector.get(ProcessService).isProcessTask()
	}

	static get isUnassignedTask() {
		return AppContext.injector.get(ProcessService).isUnassignedTask()
	}

	static get isAssignedTask() {
		return AppContext.injector.get(ProcessService).isAssignedTask()
	}
}
